import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import ChevronUp from 'mdi-react/ChevronUpIcon';
import CheckIcon from 'mdi-react/CheckIcon';
import { AppContext } from '@prisjakt/ui-app-context';
import VariantRadioButton from './components/variant-radio-button';
import { AppState } from '../../../../reducers';
import { VariantGroup, Variant as VariantType } from '../../../../types';
import { SortOptionEnum } from '../sort-options/types';
import { useViewportContext } from '../../../../components/viewport/context';

import * as S from './styles';

type VariantGroupsProps = {
  handleSelectedVariant: (
    group: VariantGroup,
    variant: VariantType | null
  ) => void;
  variantGroups: VariantGroup[];
};

const VariantGroups = ({
  handleSelectedVariant,
  variantGroups,
}: VariantGroupsProps) => (
  <>
    {variantGroups.map(group =>
      group.variants?.length ? (
        <Group handleSelectedVariant={handleSelectedVariant} group={group} />
      ) : null
    )}
  </>
);

type GroupProps = {
  group: VariantGroup;
  handleSelectedVariant: (
    group: VariantGroup,
    variant: VariantType | null
  ) => void;
};

const Group = ({ group, handleSelectedVariant }: GroupProps) => {
  const { t } = useContext(AppContext);

  const { isDesktop } = useViewportContext();

  const VISIBLE_VARIANTS_LIMIT = group.variants?.length === 7 ? 7 : 6;

  const currentSort = useSelector(
    (state: AppState) => state.areas.product.sort
  );

  const selectedVariantId = useSelector(
    (state: AppState) => state.areas.product.variant?.id
  );

  const [showAll, setShowAll] = useState(false);

  if (!group.variants?.length) return null;

  return (
    <S.VariantGroupWrapper>
      <S.VariantGroup data-test="variant-group" key={group.id}>
        {group.variants
          .filter(
            (variant, index) =>
              !isDesktop || showAll || index < VISIBLE_VARIANTS_LIMIT
          )
          .sort((a, b) => a.id - b.id)
          .map((variant, index) => {
            return (
              <VariantRadioButton
                key={`${group.type}-${variant.id}`}
                handleOnChange={() => handleSelectedVariant(group, variant)}
                title={`${variant.name} (${variant.count})`}
                id={`${group.type}-${variant.id}`}
                name={group.type}
                selected={variant.id === selectedVariantId}
                image={
                  group.type === 'color' && variant.data ? (
                    <S.Color variantData={variant.data}>
                      {variant.id === selectedVariantId ? <CheckIcon /> : null}
                    </S.Color>
                  ) : null
                }
                ariaLabel={`${group.name} - ${variant.name}`}
                order={index + 1}
                price={
                  currentSort === SortOptionEnum.PRICE_INCLUDES_SHIPPING
                    ? variant?.price?.inclShipping
                    : variant?.price?.exclShipping
                }
              />
            );
          })}

        {isDesktop && group.variants.length > VISIBLE_VARIANTS_LIMIT ? (
          <S.ShowMore
            aria-label={
              showAll
                ? t('common', 'button.show-less')
                : t('common', 'button.show-more')
            }
            type="button"
            onClick={() => setShowAll(!showAll)}
            order={VISIBLE_VARIANTS_LIMIT}
          >
            {showAll ? (
              <ChevronUp />
            ) : (
              `+${group.variants.length - VISIBLE_VARIANTS_LIMIT}`
            )}
          </S.ShowMore>
        ) : null}
      </S.VariantGroup>
    </S.VariantGroupWrapper>
  );
};

export default VariantGroups;
